import type {
  ActionRequirement,
  BlueAuthorizationAction,
  ERC20ApprovalAction,
  Transaction
} from '@morpho-org/morpho-sdk'

import { type Address, type ChainId, getChainAddresses, MathLib } from '@morpho-org/blue-sdk'
import { formatters } from '@morpho-org/ui-v6/utils'
import { abbreviateAddress } from '@repo/utils'
import { isAddressEqual } from 'viem'

import { UnsupportedRequirementError } from './requirementErrors'

const BUNDLES_LABELS = {
  blueBundlesV1: 'Blue Bundles',
  vaultBundlesV1: 'Vault Bundles',
  vaultExitBundlesV1: 'Vault Exit Bundles'
} as const

type BundlesKey = keyof typeof BUNDLES_LABELS

/** Deployed Morpho Bundles contracts on `chainId`, with their display labels. */
const getBundlesContracts = (chainId: ChainId): { address: Address; label: string }[] => {
  const { bundles } = getChainAddresses(chainId)
  if (!bundles) return []
  const addresses: Partial<Record<BundlesKey, Address>> = bundles
  return (Object.keys(BUNDLES_LABELS) as BundlesKey[]).flatMap(key => {
    const address = addresses[key]
    return address ? [{ address, label: BUNDLES_LABELS[key] }] : []
  })
}

/**
 * Gets a human-readable label for a contract address.
 * @param address - Contract address to label
 * @param chainId - Chain ID
 * @returns Human-readable label or shortened address
 */
const getLabelFromAddress = (address: Address, chainId: ChainId) => {
  const bundle = getBundlesContracts(chainId).find(b => isAddressEqual(b.address, address))
  if (bundle) return bundle.label

  const { permit2 } = getChainAddresses(chainId)
  if (permit2 && isAddressEqual(permit2, address)) return 'Permit2'

  // Return the sliced address if not found
  return `contract ${abbreviateAddress(address)}`
}

const formatAmount = (amount: bigint, token: { symbol?: string; decimals: number }) =>
  amount >= MathLib.MAX_UINT_160
    ? `∞ ${token.symbol}`
    : formatters.short
        .smallValuesWithCommas()
        .digits(2)
        .unit(token.symbol)
        .of(amount, token.decimals)

export type TransactionRequirement =
  | Transaction<ERC20ApprovalAction>
  | Transaction<BlueAuthorizationAction>
  | ActionRequirement

export const formatTransactionRequirement = (
  transaction: TransactionRequirement,
  chainId: ChainId,
  token: { decimals: number; symbol?: string }
) => {
  // Authorization requirements identify the contract authorized to act for the user.
  if (
    transaction.action.type === 'blueAuthorization' ||
    transaction.action.type === 'authorization'
  ) {
    const { authorized } = transaction.action.args
    return {
      content: `Authorize ${getLabelFromAddress(authorized, chainId)}`,
      address: authorized
    }
  }

  // Allowlist the token-approval action types that carry amount/spender. Extracting
  // inside each case keeps `args` narrowed to a type that has them, so any action type
  // without them (Midnight offer-root signatures today, anything the SDK adds later)
  // safely fails closed instead of breaking the build.
  switch (transaction.action.type) {
    case 'erc20Approval': {
      const { spender, amount } = transaction.action.args
      return {
        content: `Allow ${getLabelFromAddress(spender, chainId)} to spend ${formatAmount(amount, token)}`,
        address: spender
      }
    }
    case 'permit': {
      const { spender, amount } = transaction.action.args
      return {
        content: `Allow ${getLabelFromAddress(spender, chainId)} to spend ${formatAmount(amount, token)} (via Permit)`,
        address: spender
      }
    }
    case 'permit2SignatureTransfer': {
      const { spender, amount } = transaction.action.args
      return {
        content: `Allow ${getLabelFromAddress(spender, chainId)} to spend ${formatAmount(amount, token)} (via Permit2)`,
        address: spender
      }
    }
  }

  throw new UnsupportedRequirementError(transaction.action.type)
}
